import { useMemo, useState } from 'react'
import type { CollectionTarget } from '../../../types/collection-targets'

export type SortField = 'hostname' | 'status' | 'evidenceId' | 'priority'
export type SortDir = 'asc' | 'desc'

interface UseCollectionTargetsTableArgs {
  targets: CollectionTarget[]
  selectedIds: Set<string>
  onSelectionChange: (ids: Set<string>) => void
}

export interface CollectionTargetsTableState {
  statusFilter: string
  setStatusFilter: React.Dispatch<React.SetStateAction<string>>
  searchQuery: string
  setSearchQuery: React.Dispatch<React.SetStateAction<string>>
  sortField: SortField
  sortDir: SortDir
  filtered: CollectionTarget[]
  toggleSort: (field: SortField) => void
  toggleAll: () => void
  toggleOne: (id: string) => void
}

export function getSortValue(target: CollectionTarget, field: SortField): string {
  switch (field) {
    case 'hostname':
      return target.endpointHostname
    case 'status':
      return target.targetStatus
    case 'evidenceId':
      return target.evidenceId ?? ''
    case 'priority':
      return target.priority
  }
}

interface FilterAndSortArgs {
  targets: CollectionTarget[]
  statusFilter: string
  searchQuery: string
  sortField: SortField
  sortDir: SortDir
}

function filterAndSortTargets(args: FilterAndSortArgs): CollectionTarget[] {
  const { targets, statusFilter, searchQuery, sortField, sortDir } = args
  let result = targets
  if (statusFilter) result = result.filter((tgt) => tgt.targetStatus === statusFilter)
  if (searchQuery) {
    const query = searchQuery.toLowerCase()
    result = result.filter(
      (tgt) =>
        tgt.endpointHostname.toLowerCase().includes(query) ||
        tgt.evidenceId?.toLowerCase().includes(query)
    )
  }
  return [...result].sort((left, right) => {
    const cmp = getSortValue(left, sortField).localeCompare(getSortValue(right, sortField))
    return sortDir === 'asc' ? cmp : -cmp
  })
}

export function useCollectionTargetsTable(
  args: UseCollectionTargetsTableArgs
): CollectionTargetsTableState {
  const { targets, selectedIds, onSelectionChange } = args
  const [statusFilter, setStatusFilter] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [sortField, setSortField] = useState<SortField>('hostname')
  const [sortDir, setSortDir] = useState<SortDir>('asc')

  const filtered = useMemo(
    () => filterAndSortTargets({ targets, statusFilter, searchQuery, sortField, sortDir }),
    [targets, statusFilter, searchQuery, sortField, sortDir]
  )

  const toggleSort = (field: SortField): void => {
    if (sortField === field) setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'))
    else {
      setSortField(field)
      setSortDir('asc')
    }
  }

  const toggleAll = (): void => {
    if (selectedIds.size === filtered.length) onSelectionChange(new Set())
    else onSelectionChange(new Set(filtered.map((tgt) => tgt.id)))
  }

  const toggleOne = (id: string): void => {
    const next = new Set(selectedIds)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    onSelectionChange(next)
  }

  return {
    statusFilter,
    setStatusFilter,
    searchQuery,
    setSearchQuery,
    sortField,
    sortDir,
    filtered,
    toggleSort,
    toggleAll,
    toggleOne,
  }
}
